import {
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { usePage } from "@inertiajs/react";
import {
  WebsiteAPIService,
  type GetWebsiteResponse,
  type ListPagesResponse,
} from "@rails_api_base";
import { useWebsiteId as useCoreWebsiteId } from "~/stores/projectStore";
import { useJwt, useRootPath } from "~/stores/sessionStore";

// Re-export for backwards compatibility
export { WebsiteAPIService as WebsiteService } from "@rails_api_base";

// ============================================================================
// Query Keys
// ============================================================================

export const websiteKeys = {
  all: ["websites"] as const,
  details: () => [...websiteKeys.all, "detail"] as const,
  detail: (websiteId: number) => [...websiteKeys.details(), websiteId] as const,
  pages: (websiteId: number) => [...websiteKeys.all, "pages", websiteId] as const,
};

// ============================================================================
// Service Hook
// ============================================================================

/**
 * Hook that provides a memoized WebsiteService instance
 * Reads from sessionStore instead of page props - stores are hydrated in SiteLayout.
 */
export function useWebsiteService() {
  const jwt = useJwt();
  const rootPath = useRootPath();
  return useMemo(
    () => new WebsiteAPIService({ jwt: jwt ?? "", baseUrl: rootPath ?? "" }),
    [jwt, rootPath]
  );
}

/**
 * Hook to get the current website ID from the core entity store.
 * The store is populated from page props and Langgraph state.
 */
function useWebsiteId(): number | null {
  return useCoreWebsiteId();
}

// ============================================================================
// Query Hooks
// ============================================================================

type WebsiteQueryOptions = Omit<UseQueryOptions<GetWebsiteResponse, Error>, "queryKey" | "queryFn">;

/**
 * Hook for fetching website data with caching.
 * First checks Inertia page props for initial data, then falls back to API.
 *
 * @example
 * ```tsx
 * const { data: website, isLoading, error } = useWebsite();
 * ```
 */
export function useWebsite(options?: WebsiteQueryOptions) {
  const service = useWebsiteService();
  const websiteId = useWebsiteId();
  // Get initial data from Inertia props if available
  const { website: initialWebsite } = usePage<{ website?: GetWebsiteResponse }>().props;

  return useQuery({
    queryKey: websiteKeys.detail(websiteId ?? 0),
    queryFn: () => service.get(websiteId!),
    enabled: !!websiteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Use Inertia props as initial data if available
    initialData: initialWebsite ?? undefined,
    ...options,
  });
}

type PagesQueryOptions = Omit<
  UseQueryOptions<ListPagesResponse, Error>,
  "queryKey" | "queryFn"
>;

/**
 * Fetch the wired pages for a website. Pages are derived from `src/pages/*.astro`
 * files.
 *
 * Invalidated by `useWebsitePagesSync` on stream-complete so tabs refresh
 * after the agent adds or removes a page file.
 */
export function useWebsitePages(
  websiteIdArg?: number | null,
  options?: PagesQueryOptions
) {
  const service = useWebsiteService();
  const currentWebsiteId = useWebsiteId();
  const websiteId = websiteIdArg ?? currentWebsiteId;

  return useQuery({
    queryKey: websiteKeys.pages(websiteId ?? 0),
    queryFn: () => service.listPages(websiteId!),
    enabled: !!websiteId,
    staleTime: 30_000,
    ...options,
  });
}
